import { doc, collection, setDoc, deleteDoc, getDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '@/firebase'
import { RULE_EXPECTATIONS, attempt, accessDetail, fmtExpected } from '../ruleExpectations.js'

// Testing module: accommodations are Cloud-Function-only; attendance and
// session notes are admin / testingAccess; calendar events may be created
// by the event's own teacherId or a coordinator. Read of a missing
// accommodations doc is not probed — inScope(resource.data) denies everyone
// when the mirror does not exist, which would look like a role check.

async function ownTestingAccess(uid) {
  try {
    const snap = await getDoc(doc(db, 'users', uid))
    return snap.exists() && snap.data()?.testingAccess === true
  } catch {
    return false
  }
}

export default {
  id: 'testingModule',
  label: 'Testing module writes',
  description:
    'testingAccommodations stay function-only; attendance, session notes, and calendar events follow testingAccess / coordinator rules.',
  destructive: true,

  async run(ctx, step) {
    const role = ctx.roleClaim
    const schoolTier = Array.isArray(ctx.tokenSchoolIds)
    if (schoolTier && !ctx.schoolId) {
      step.block(
        'testing module writes',
        'Select a school before probing school-scoped testing collections'
      )
      return
    }

    const testingAccess = await ownTestingAccess(ctx.user.uid)
    // Every testing write also passes through inScope(request.resource.data):
    // an unstamped non-district token (e.g. district_coordinator) is denied by
    // scope even when the role predicate passes.
    const scopeOk = RULE_EXPECTATIONS.inScopeCreate(role, {
      tokenSchoolIds: ctx.tokenSchoolIds,
      schoolId: ctx.schoolId
    })
    const expectAccom = RULE_EXPECTATIONS.testingAccommodationsWrite()
    const expectAttend =
      RULE_EXPECTATIONS.testingAttendanceWrite(role, { testingAccess }) && scopeOk
    const expectNotes =
      RULE_EXPECTATIONS.testingSessionNotesWrite(role, { testingAccess }) && scopeOk
    const expectCal =
      RULE_EXPECTATIONS.testingCalendarWrite(role, { isOwnTeacher: true }) && scopeOk

    const accomRef = doc(db, 'testingAccommodations', `smoke_accom_${ctx.runId.slice(-8)}`)
    const attendRef = doc(collection(db, 'testingAttendance'))
    const notesRef = doc(collection(db, 'testingSessionNotes'))
    const calRef = doc(collection(db, 'testingCalendarEvents'))

    try {
      await step(
        `write testing accommodations (expected: ${fmtExpected(expectAccom)})`,
        async () => {
          const { allowed } = await attempt(() =>
            setDoc(accomRef, {
              studentId: accomRef.id,
              __smokeTest: true,
              __smokeRunId: ctx.runId,
              ...(ctx.schoolId ? { schoolId: ctx.schoolId } : {})
            })
          )
          if (allowed) await deleteDoc(accomRef).catch(() => {})
          return accessDetail(expectAccom, allowed)
        }
      )

      let attendCreated = false
      await step(`create testing attendance (expected: ${fmtExpected(expectAttend)})`, async () => {
        const { allowed } = await attempt(() =>
          setDoc(attendRef, {
            teacherId: ctx.user.uid,
            caseManagerId: '',
            scheduleTeacherIds: [],
            present: true,
            __smokeTest: true,
            __smokeRunId: ctx.runId,
            createdAt: serverTimestamp(),
            ...(ctx.schoolId ? { schoolId: ctx.schoolId } : {})
          })
        )
        attendCreated = allowed
        return allowed
          ? `${accessDetail(expectAttend, allowed)} — testingAttendance/${attendRef.id}`
          : accessDetail(expectAttend, allowed)
      })
      if (attendCreated) {
        await step(
          `delete testing attendance (expected: ${fmtExpected(expectAttend)})`,
          async () => {
            const { allowed } = await attempt(() => deleteDoc(attendRef))
            if (allowed) attendCreated = false
            return accessDetail(expectAttend, allowed)
          }
        )
      } else {
        step.skip('delete testing attendance', 'Create denied — nothing to delete')
      }

      let notesCreated = false
      await step(
        `create testing session note (expected: ${fmtExpected(expectNotes)})`,
        async () => {
          const { allowed } = await attempt(() =>
            setDoc(notesRef, {
              note: 'SMOKE',
              __smokeTest: true,
              __smokeRunId: ctx.runId,
              createdAt: serverTimestamp(),
              ...(ctx.schoolId ? { schoolId: ctx.schoolId } : {})
            })
          )
          notesCreated = allowed
          return allowed
            ? `${accessDetail(expectNotes, allowed)} — testingSessionNotes/${notesRef.id}`
            : accessDetail(expectNotes, allowed)
        }
      )
      if (notesCreated) {
        await step(
          `delete testing session note (expected: ${fmtExpected(expectNotes)})`,
          async () => {
            const { allowed } = await attempt(() => deleteDoc(notesRef))
            if (allowed) notesCreated = false
            return accessDetail(expectNotes, allowed)
          }
        )
      } else {
        step.skip('delete testing session note', 'Create denied — nothing to delete')
      }

      let calCreated = false
      await step(
        `create own testing calendar event (expected: ${fmtExpected(expectCal)})`,
        async () => {
          const schoolId = ctx.schoolId || 'smoke'
          const { allowed } = await attempt(() =>
            setDoc(calRef, {
              schoolId,
              teacherId: ctx.user.uid,
              date: '2026-09-15',
              title: 'SMOKE calendar',
              __smokeTest: true,
              __smokeRunId: ctx.runId
            })
          )
          calCreated = allowed
          return allowed
            ? `${accessDetail(expectCal, allowed)} — testingCalendarEvents/${calRef.id}`
            : accessDetail(expectCal, allowed)
        }
      )
      if (calCreated) {
        await step(
          `delete own testing calendar event (expected: ${fmtExpected(expectCal)})`,
          async () => {
            const { allowed } = await attempt(() => deleteDoc(calRef))
            if (allowed) calCreated = false
            return accessDetail(expectCal, allowed)
          }
        )
      } else {
        step.skip('delete testing calendar event', 'Create denied — nothing to delete')
      }
    } finally {
      await deleteDoc(accomRef).catch(() => {})
      await deleteDoc(attendRef).catch(() => {})
      await deleteDoc(notesRef).catch(() => {})
      await deleteDoc(calRef).catch(() => {})
    }
  }
}
